import { SignInData, SignUpData } from '@rminte/altai-service';
import { Button, FormField, Icon, Nav, NavLink } from 'boot-cell';
import { observable } from 'mobx';
import { component, observer, WebCell, WebCellProps } from 'web-cell';
import { formToJSON } from 'web-utility';

import userStore from '../model/User';

export interface SessionFormProps extends WebCellProps {
    onSignIn: (event: CustomEvent<SignInData | undefined>) => any;
}

export interface SessionForm extends WebCell<SessionFormProps> {}

interface SignUpInput extends SignUpData {
    repeat_password: string;
}

@component({ tagName: 'session-form' })
@observer
export class SessionForm extends HTMLElement {
    @observable
    accessor signType: 'up' | 'in' = 'in';

    handleOTP = async (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();

        const { email } = formToJSON<SignInData>(
            (event.target as HTMLButtonElement).form
        );
        await userStore.sendOTP(email);

        alert('Check your email for the one-time password');
    };

    handleWebAuthn = async (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();

        if (this.signType === 'up') {
            const { email } = formToJSON<SignInData>(
                (event.currentTarget as HTMLButtonElement).form
            );
            if (!email)
                throw new Error('Email is required for WebAuthn sign-up');

            await userStore.signUpWebAuthn(email);
        } else await userStore.signInWebAuthn();

        this.emit('signin');
    };

    handleSubmit = async (event: Event) => {
        event.preventDefault();
        event.stopPropagation();

        const { email, password, repeat_password } = formToJSON<SignUpInput>(
            event.target as HTMLFormElement
        );
        if (this.signType === 'up') {
            if (password !== repeat_password)
                throw new Error('Passwords do not match');

            await userStore.signUp(email, password);

            this.signType = 'in';
        } else {
            await userStore.signIn(email, password);

            this.emit('signin', { email, password });
        }
    };

    renderPasswordField = (signType = this.signType, loading = false) => (
        <div className="d-flex flex-column justify-content-center gap-2">
            <FormField
                as="input"
                type="password"
                name="password"
                required
                autofocus
                placeholder="Password"
                labelFloat
            />
            {signType === 'up' ? (
                <FormField
                    as="input"
                    type="password"
                    name="repeat_password"
                    required
                    autofocus
                    placeholder="Repeat Password"
                    labelFloat
                />
            ) : (
                <Button
                    type="button"
                    variant="secondary"
                    disabled={loading}
                    onClick={this.handleOTP}
                >
                    Send One-Time Password
                </Button>
            )}
        </div>
    );

    render() {
        const { signType } = this,
            loading = userStore.uploading > 0;

        return (
            <form
                className="d-flex flex-column justify-content-center gap-2"
                onSubmit={this.handleSubmit}
            >
                <Nav className="nav-pills nav-justified">
                    <NavLink
                        active={signType === 'up'}
                        onClick={() => (this.signType = 'up')}
                    >
                        Sign up
                    </NavLink>
                    <NavLink
                        active={signType === 'in'}
                        onClick={() => (this.signType = 'in')}
                    >
                        Sign in
                    </NavLink>
                </Nav>
                <FormField
                    as="input"
                    type="email"
                    name="email"
                    required
                    autofocus
                    placeholder="Email address"
                    labelFloat
                />
                <div className="d-flex align-items-center gap-2">
                    {this.renderPasswordField(signType, loading)}

                    <Button
                        type="button"
                        variant="secondary"
                        disabled={loading}
                        onClick={this.handleWebAuthn}
                    >
                        <Icon name="fingerprint" size={3} />
                    </Button>
                </div>
                <Button
                    type="submit"
                    variant="primary"
                    className="d-block w-100"
                    size="lg"
                    disabled={loading}
                >
                    Sign {signType}
                </Button>
            </form>
        );
    }
}
